import { memo } from "react";
import { Checkbox } from "../../components/Checkbox";
import { StarIcon } from "../../assets/StarIcon";
import type { TrustedContact } from "../../types/trusted-contacts";
import { CONTACT_TYPE_MODIFIER, contactTypeLabel } from "./contactTypes";

interface TrustedContactRowProps {
  contact: TrustedContact;
  isSelected: boolean;
  /** False when this build or this coach has no messaging, which hides the row's message action. */
  canMessage: boolean;
  /**
   * True while this row's own favorite toggle is in flight. Scoped to this one contact, not a
   * single flag shared by the table — starring several rows in a row must not grey out the ninth
   * one while the third is still saving.
   */
  favoritePending: boolean;
  onToggle: (id: string) => void;
  onToggleFavorite: (contact: TrustedContact) => void;
  onMessage: (contact: TrustedContact) => void;
  onEdit: (contact: TrustedContact) => void;
  onDelete: (contact: TrustedContact) => void;
}

/**
 * One directory row.
 *
 * Memoised and given primitive props rather than closures over the selection set, so ticking one
 * checkbox re-renders one row instead of the page.
 */
export const TrustedContactRow = memo(
  ({
    contact,
    isSelected,
    canMessage,
    favoritePending,
    onToggle,
    onToggleFavorite,
    onMessage,
    onEdit,
    onDelete,
  }: TrustedContactRowProps) => (
    <tr>
      <td className="trusted-contacts__select-cell">
        <Checkbox
          checked={isSelected}
          onChange={() => onToggle(contact.id)}
          aria-label={`Select ${contact.name}`}
        />
      </td>
      <td className="trusted-contacts__favorite-cell">
        <button
          type="button"
          className="trusted-contacts__favorite-button"
          onClick={() => onToggleFavorite(contact)}
          disabled={favoritePending}
          aria-pressed={contact.isFavorite}
          aria-label={
            contact.isFavorite ? `Unfavorite ${contact.name}` : `Favorite ${contact.name}`
          }
        >
          <StarIcon
            className={`trusted-contacts__favorite-icon ${
              contact.isFavorite
                ? "trusted-contacts__favorite-icon--filled"
                : "trusted-contacts__favorite-icon--outline"
            }`}
            filled={contact.isFavorite}
            size={18}
          />
        </button>
      </td>
      <td className="trusted-contacts__name">{contact.name}</td>
      <td className="data-table__muted">{contact.organizationName ?? "—"}</td>
      <td className="data-table__muted">{contact.state ?? "—"}</td>
      <td>
        <span
          className={`trusted-contacts__badge trusted-contacts__badge--${
            CONTACT_TYPE_MODIFIER[contact.contactType] ?? "unknown"
          }`}
        >
          {contactTypeLabel(contact.contactType)}
        </span>
      </td>
      <td className="data-table__muted">
        {contact.phone ? (
          <a
            className="trusted-contacts__link"
            href={`tel:${contact.phoneE164 ?? contact.phone}`}
          >
            {contact.phone}
          </a>
        ) : (
          "—"
        )}
      </td>
      <td className="data-table__muted">
        {contact.email ? (
          <a className="trusted-contacts__link" href={`mailto:${contact.email}`}>
            {contact.email}
          </a>
        ) : (
          "—"
        )}
      </td>
      <td className="trusted-contacts__notes">{contact.notes ?? ""}</td>
      <td className="data-table__actions">
        <div className="trusted-contacts__row-actions">
          {canMessage && (
            /*
              aria-disabled rather than disabled: a disabled button leaves the tab order, so a
              screen reader user could never reach the label that explains why it is unavailable.
              The handler stays live and says so instead.
            */
            <button
              type="button"
              className="trusted-contacts__icon-button"
              onClick={() => onMessage(contact)}
              aria-disabled={!contact.phoneE164}
              aria-label={
                contact.phoneE164
                  ? `Message ${contact.name}`
                  : `${contact.name} has no phone number saved`
              }
              title={contact.phoneE164 ? "Message" : "No phone number saved"}
            >
              💬
            </button>
          )}
          <button
            type="button"
            className="trusted-contacts__text-button"
            onClick={() => onEdit(contact)}
          >
            Edit
            <span className="trusted-contacts__sr-only"> {contact.name}</span>
          </button>
          <button
            type="button"
            className="trusted-contacts__text-button trusted-contacts__text-button--danger"
            onClick={() => onDelete(contact)}
          >
            Delete
            <span className="trusted-contacts__sr-only"> {contact.name}</span>
          </button>
        </div>
      </td>
    </tr>
  )
);

TrustedContactRow.displayName = "TrustedContactRow";
